import { ReactNode, useCallback, useMemo } from 'react'
import { BsChevronRight } from 'react-icons/bs'
import {
  generatePath,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router-dom'

import styled from 'styled-components'

import { Colours } from '@lib/theme'
import * as Routes from '@piv/portal/app/routes'

export interface Props {
  title: string
  subtitle: string | ReactNode
  dataPackageKey: string
}

export const DataPackageList = styled.ul`
  padding: 0;
  margin: 0;

  > * {
    margin-bottom: 0.2rem;
    padding: 0.2rem 0.4rem;
  }
`

const RowContainer = styled.li`
  list-style: none;

  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.4rem;
  /* We need this to prevent items "moving" when a border renders on an active item */
  border: 1px solid transparent;
  margin: 0 -0.5rem;

  :hover,
  &.active {
    background-color: #fff;
    .result-arrow {
      display: unset;
    }
  }

  .result-arrow {
    display: none;
  }
`

const InnerRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: stretch;

  > div {
    flex: 1 1 0;
    margin-right: 2rem;
    padding: 0.1rem;
  }

  h5 {
    margin: 0;
    color: ${Colours.FgShade.Dark};
    font-weight: 700;
  }
`

export function DataPackageRow({ title, subtitle, dataPackageKey }: Props) {
  const location = useLocation()
  const navigate = useNavigate()
  const path = useMemo(
    () =>
      generatePath(Routes.Explore.Detail, {
        id: dataPackageKey,
      }),
    [dataPackageKey]
  )
  const isActive = useMatch(path)

  const handleRowClick = useCallback(() => {
    navigate({ pathname: path, search: location.search })
  }, [location.search, navigate, path])

  return (
    <RowContainer
      id={`data-package-${dataPackageKey}`}
      onClick={handleRowClick}
      className={isActive ? 'active' : ''}
    >
      <InnerRow>
        <div>
          <h5>{title}</h5>
          <small>{subtitle}</small>
        </div>
        <BsChevronRight
          className="result-arrow"
          strokeWidth="2px"
          color={isActive ? Colours.Brand.BlueMid : ''}
        />
      </InnerRow>
    </RowContainer>
  )
}
